/**
 * Podcast detail view component - shows podcast info and episodes
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useToast, ToastContainer } from '../common/Toast';
import podcastService from '../../services/podcastService';
import EpisodeDetailView from './EpisodeDetailView';

const PodcastDetailView = ({ podcast, onBack, onUpdate }) => {
  const { toasts, showToast, removeToast } = useToast();
  
  // State
  const [episodes, setEpisodes] = useState([]);
  const [totalEpisodes, setTotalEpisodes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [transcribingStatus, setTranscribingStatus] = useState({});
  const [refreshingEpisodes, setRefreshingEpisodes] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState(null);

  // Load episodes
  const loadEpisodes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await podcastService.getEpisodes(podcast.id, { limit: 50, skip: 0 });
      
      if (response.success) {
        const episodesList = response.episodes || response.data?.episodes || [];
        const total = response.total || response.data?.total || 0;
        
        setEpisodes(episodesList);
        setTotalEpisodes(total);
      } else {
        console.error('Failed to load episodes:', response);
        setEpisodes([]);
        setTotalEpisodes(0);
      }
    } catch (error) {
      console.error('Error loading episodes:', error);
      setEpisodes([]);
      setTotalEpisodes(0);
    } finally {
      setLoading(false);
    }
  }, [podcast.id]);

  // Load episodes on mount
  useEffect(() => {
    loadEpisodes();
  }, [loadEpisodes]);

  // Handle refresh episodes
  const handleRefreshEpisodes = useCallback(async (forceRefresh = false) => {
    try {
      setRefreshingEpisodes(true);
      showToast('Refreshing episodes from RSS feed...', 'info');
      
      const response = await podcastService.refreshEpisodes(podcast.id, forceRefresh);
      
      if (response && response.success) {
        const newEpisodes = response.new_episodes || response.data?.new_episodes || 0;
        
        if (newEpisodes > 0) {
          showToast(`✅ Found ${newEpisodes} new episodes!`, 'success');
          loadEpisodes(); // Refresh episodes list
        } else {
          showToast('✅ Episodes refreshed - no new episodes found', 'success');
        }
        
        // Update parent podcast data
        if (onUpdate) {
          const updatedPodcast = { ...podcast, last_refresh_at: new Date().toISOString() };
          onUpdate(updatedPodcast);
        }
      } else {
        const errorMsg = response?.error || response?.message || 'Refresh failed';
        showToast(`❌ Refresh failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error refreshing episodes:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Refresh error: ${errorMessage}`, 'error');
    } finally {
      setRefreshingEpisodes(false);
    }
  }, [podcast.id, podcast, onUpdate, showToast, loadEpisodes]);

  // Handle transcribe episode
  const handleTranscribeEpisode = useCallback(async (episodeId, episodeTitle, forceRetranscribe = false) => {
    try {
      setTranscribingStatus(prev => ({ ...prev, [episodeId]: true }));
      showToast(`Transcribing episode: ${episodeTitle}...`, 'info');
      
      const response = await podcastService.transcribeEpisode(episodeId, forceRetranscribe);
      
      if (response && response.success) {
        const transcriptLength = response.transcript_length || response.data?.transcript_length || 0;
        showToast(`✅ Episode transcribed successfully! (${transcriptLength} characters)`, 'success');
        loadEpisodes(); // Refresh to show updated status
      } else {
        const errorMsg = response?.error || response?.message || 'Transcription failed';
        showToast(`❌ Transcription failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error transcribing episode:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Transcription error: ${errorMessage}`, 'error');
    } finally {
      setTranscribingStatus(prev => ({ ...prev, [episodeId]: false }));
    }
  }, [showToast, loadEpisodes]);

  // Handle view episode details
  const handleViewEpisode = (episode) => {
    setSelectedEpisode(episode);
  };

  // Handle back from episode detail view
  const handleBackFromEpisodeDetail = () => {
    setSelectedEpisode(null);
    loadEpisodes(); // Refresh episodes list when returning
  };

  // Handle episode update from detail view
  const handleEpisodeUpdate = (updatedEpisode) => {
    setEpisodes(prev => prev.map(episode => 
      episode.id === updatedEpisode.id ? updatedEpisode : episode
    ));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  const formatDuration = (seconds) => {
    return podcastService.formatDuration(seconds);
  };

  // If viewing a specific episode, show episode detail view
  if (selectedEpisode) {
    return (
      <EpisodeDetailView
        episode={selectedEpisode}
        podcast={podcast}
        onBack={handleBackFromEpisodeDetail}
        onUpdate={handleEpisodeUpdate}
      />
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex items-center space-x-3">
              {podcast.podcast_image_url ? (
                <img src={podcast.podcast_image_url} alt="" className="w-10 h-10 rounded object-cover" />
              ) : (
                <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                  <span className="text-xl">🎧</span>
                </div>
              )}
              <div>
                <h1 className="text-lg font-semibold text-gray-900">{podcast.podcast_name}</h1>
                <p className="text-sm text-gray-600">{podcast.podcast_title || 'No title available'}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => handleRefreshEpisodes(true)}
              disabled={refreshingEpisodes}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center space-x-2"
            >
              {refreshingEpisodes ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                  <span>Refreshing...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Refresh Episodes</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Podcast Info */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">RSS Feed</h3>
            <a 
              href={podcast.rss_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-800 truncate block"
            >
              {podcast.rss_url}
            </a>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">Status</h3>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              podcast.status === 'active' 
                ? 'bg-green-100 text-green-800'
                : podcast.status === 'error' 
                ? 'bg-red-100 text-red-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {podcast.status || 'pending'}
            </span>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">Episodes</h3>
            <p className="text-sm text-gray-900">
              {podcast.transcribed_episodes || 0} transcribed / {podcast.total_episodes || 0} total
            </p>
          </div>
        </div>
        
        {podcast.podcast_description && (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Description</h3>
            <p className="text-sm text-gray-900">{podcast.podcast_description}</p>
          </div>
        )}
      </div>

      {/* Episodes List */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading episodes...</p>
            </div>
          </div>
        ) : episodes.length > 0 ? (
          <div className="h-full flex flex-col">
            {/* Episodes Header */}
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center text-xs font-medium text-gray-700">
              <div className="flex-1 min-w-0 mr-4">Episode Title</div>
              <div className="w-20 text-center flex-shrink-0 mr-4">Duration</div>
              <div className="w-24 text-center flex-shrink-0 mr-4">Published</div>
              <div className="w-20 text-center flex-shrink-0 mr-4">Transcribed</div>
              <div className="w-20 text-center flex-shrink-0 mr-4">Article</div>
              <div className="w-32 text-center flex-shrink-0">Action</div>
            </div>
            
            {/* Episodes */}
            <div className="flex-1 overflow-auto bg-white">
              {episodes.map((episode) => (
                <div key={episode.id} className="px-4 py-3 border-b border-gray-200 flex items-center hover:bg-gray-50">
                  {/* Episode Title */}
                  <div className="flex-1 min-w-0 mr-4">
                    <div 
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer truncate"
                      onClick={() => handleViewEpisode(episode)}
                    >
                      {episode.episode_title}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {episode.episode_description?.substring(0, 80)}...
                    </div>
                  </div>
                  
                  {/* Duration */}
                  <div className="w-20 text-center flex-shrink-0 mr-4">
                    <div className="text-xs text-gray-600">
                      {formatDuration(episode.duration_seconds)}
                    </div>
                  </div>
                  
                  {/* Published Date */}
                  <div className="w-24 text-center flex-shrink-0 mr-4">
                    <div className="text-xs text-gray-600">
                      {formatDate(episode.published_at)}
                    </div>
                  </div>
                  
                  {/* Transcription Status */}
                  <div className="w-20 text-center flex-shrink-0 mr-4">
                    {episode.transcription_completed ? (
                      <span className="text-green-600 text-lg" title="Transcribed">✓</span>
                    ) : episode.transcription_status === 'in_progress' ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                    ) : (
                      <span className="text-red-500 text-lg" title="Not transcribed">✗</span>
                    )}
                  </div>
                  
                  {/* Article Status */}
                  <div className="w-20 text-center flex-shrink-0 mr-4">
                    {episode.article_drafted ? (
                      <span className="text-green-600 text-lg" title="Article drafted">✓</span>
                    ) : (
                      <span className="text-red-500 text-lg" title="No article yet">✗</span>
                    )}
                  </div>
                  
                  {/* Action Button */}
                  <div className="w-32 flex items-center justify-center flex-shrink-0">
                    {!episode.transcription_completed ? (
                      <button 
                        onClick={() => handleTranscribeEpisode(episode.id, episode.episode_title)}
                        disabled={transcribingStatus[episode.id]}
                        className="px-3 py-1.5 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 disabled:bg-purple-400 flex items-center space-x-1"
                      >
                        {transcribingStatus[episode.id] ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                            <span>Transcribing...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 6.464a5 5 0 010 7.072M12 9v6m3 0a9 9 0 01-6 0m6 0v1a2 2 0 01-2 2H9a2 2 0 01-2-2v-1m6 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v0" />
                            </svg>
                            <span>Transcribe</span>
                          </>
                        )}
                      </button>
                    ) : !episode.article_drafted ? (
                      <button 
                        className="px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                        title="Generate article (coming soon)"
                      >
                        Generate Article
                      </button>
                    ) : (
                      <span className="text-xs text-gray-500">Complete</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Results Summary */}
            <div className="px-4 py-2 bg-white border-t border-gray-200">
              <div className="text-xs text-gray-700">
                Showing {episodes.length} of {totalEpisodes} episodes
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <svg className="w-16 h-16 mb-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M15.536 6.464a5 5 0 010 7.072M12 9v6m3 0a9 9 0 01-6 0m6 0v1a2 2 0 01-2 2H9a2 2 0 01-2-2v-1m6 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v0" />
              </svg>
              <h3 className="text-lg font-medium mb-2">No episodes found</h3>
              <p className="text-center mb-4">Refresh episodes to discover content from the RSS feed.</p>
              
              <button
                onClick={() => handleRefreshEpisodes(true)}
                disabled={refreshingEpisodes}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
              >
                {refreshingEpisodes ? 'Refreshing...' : 'Refresh Episodes Now'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

export default PodcastDetailView;